"use client";

import React, { useRef, useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import * as PayPalCountries from "@/lib/paypal-countries";
import SearchableDropdown from "@/components/ui/SearchableDropdown";
import PhoneInput from "@/components/AuthComponents/AuthUI/phone-number-input";
import { Switch } from "@/components/ui/switch";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { isStateRequiredForCountry } from "@kraahl/contract";
import { listAddresses } from "@/lib/address-book/list-addresses";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

export type ShippingFormData = {
  firstName: string;
  lastName: string;
  address: string;
  apartment: string;
  city: string;
  postalCode: string;
  country: string;
  state: string;
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
};

interface SavedAddress {
  id: string;
  label: string;
  first_name?: string;
  last_name?: string;
  address: string;
  apartment?: string;
  city: string;
  state?: string;
  postal: string;
  country: string;
  phone?: string;
  email?: string;
  is_main: boolean;
}

type ShippingFormProps = {
  onFormDataChangeAction: (isValid: boolean, formData: ShippingFormData, saveAddress?: boolean) => void;
  onCountryChangeAction?: (countryCode: string) => void;
  profile?: { id?: string; email?: string; phone?: string } | null;
  userId?: string;
  deferKeyboardFocus?: boolean;
};

type ManualAddressDraft = {
  data: ShippingFormData;
  country: any;
  state: any;
  stateOptions: any[];
  shouldSave: boolean;
};

const SHIPPING_DRAFT_KEY = "kraahl:shipping_draft";

function saveShippingDraft(data: ShippingFormData) {
  try {
    // Don't persist email/phone — those come from profile
    const { email, phoneCountryCode, phoneNumber, ...draft } = data;
    sessionStorage.setItem(SHIPPING_DRAFT_KEY, JSON.stringify(draft));
  } catch { /* quota exceeded or SSR — ignore */ }
}

function loadShippingDraft(): Partial<ShippingFormData> | null {
  try {
    const raw = sessionStorage.getItem(SHIPPING_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Basic sanity check
    if (typeof parsed === 'object' && parsed !== null && typeof parsed.firstName === 'string') {
      return parsed;
    }
    return null;
  } catch { return null; }
}

export function clearShippingDraft() {
  try { sessionStorage.removeItem(SHIPPING_DRAFT_KEY); } catch { /* ignore */ }
}

function parsePhone(raw?: string | null): { code: string; number: string } {
  if (!raw || !raw.startsWith("+")) return { code: "+1", number: "" };
  const parsed = parsePhoneNumberFromString(raw);
  if (parsed) {
    return { code: `+${parsed.countryCallingCode}`, number: parsed.nationalNumber.toString() };
  }
  // Fallback: country codes are 1-4 digits
  const match = raw.match(/^\+(\d{1,4})/);
  return match
    ? { code: `+${match[1]}`, number: raw.slice(match[0].length) }
    : { code: "+1", number: "" };
}

/** Returns a list of human-readable field keys that are missing/invalid for the given saved address.
 *  Mirrors the validation logic from isShippingFormComplete (checkout-form.tsx) + state check for US/CA. */
function getMissingSavedAddressFields(
  address: SavedAddress,
  profile?: { email?: string | null; phone?: string | null } | null,
): string[] {
  const missing: string[] = [];
  if (!address.first_name?.trim()) missing.push("firstName");
  if (!address.last_name?.trim()) missing.push("lastName");
  if (!address.address?.trim()) missing.push("address");
  if (!address.city?.trim()) missing.push("city");
  if (!address.postal?.trim()) missing.push("postalCode");
  if (!address.country?.trim()) missing.push("country");
  if (isStateRequiredForCountry(address.country) && !address.state?.trim()) missing.push("state");
  const email = address.email?.trim() || profile?.email?.trim() || "";
  if (!email) missing.push("email");
  const phoneRaw = address.phone?.trim() || profile?.phone?.trim() || "";
  const { number: phoneNumber } = parsePhone(phoneRaw || undefined);
  if (!phoneRaw || phoneNumber.trim().length < 4) missing.push("phone");
  return missing;
}

const createEmptyFormData = (profile?: { email?: string | null; phone?: string | null }): ShippingFormData => {
  const { code: phoneCountryCode, number: phoneNumber } = parsePhone(profile?.phone);

  return {
    firstName: "",
    lastName: "",
    address: "",
    apartment: "",
    city: "",
    postalCode: "",
    country: "",
    state: "",
    email: profile?.email ?? "",
    phoneCountryCode,
    phoneNumber,
  };
};

const formatAddressPreview = (address: SavedAddress) => {
  const stateValue = address.state ? PayPalCountries.getStateName(address.state, address.country) : "";
  const segments = [
    address.address,
    address.city,
    stateValue,
    address.postal,
    PayPalCountries.getCountryName(address.country),
    address.phone,
  ].filter((segment) => segment && segment.trim().length > 0);

  return segments.join(", ");
};

/**
 * The overlay-panel input dialect. Every host of this form is an overlay that
 * owns a `--sheet*` surface (mobile `DockSheet`, desktop `DialogCard`, the
 * hand-rolled desktop cart drawer), so the fields sit ON the panel rather than
 * on the page: `bg-sheet` (not `bg-background`) is what makes them flush with
 * it. Formerly gated behind a `darkInputs` prop with hardcoded neutrals; the
 * tokens paint the identical dark values today and follow the panel when it
 * turns light (Phase 2 step 2f).
 * @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §1 (token ladder)
 */
const sheetInputClass =
  "rounded-3xl border-sheet-handle bg-sheet text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-muted-foreground/70 focus-visible:ring-offset-2 focus-visible:ring-offset-sheet";
const sheetLabelClass = "text-foreground/90";

export function ShippingForm({
  onFormDataChangeAction,
  onCountryChangeAction,
  profile,
  userId,
  deferKeyboardFocus = false,
}: ShippingFormProps) {
  const t = useTranslations("shippingForm");
  const containerRef = useRef<HTMLDivElement>(null);
  const [inputsEditable, setInputsEditable] = useState(!deferKeyboardFocus);
  const manualAddressDraftRef = useRef<ManualAddressDraft | null>(null);
  const profileRef = useRef(profile);
  // Tracks whether we auto-switched to manual mode due to an incomplete saved address.
  // Reset whenever the user explicitly picks a different address from the dropdown.
  const autoSwitchedRef = useRef(false);
  const [incompleteAddressWarning, setIncompleteAddressWarning] = useState(false);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  useEffect(() => {
    if (deferKeyboardFocus) setInputsEditable(false);
  }, [deferKeyboardFocus]);

  const unlockInputs = (event?: React.PointerEvent<HTMLInputElement>) => {
    setInputsEditable(true);
    if (event?.currentTarget instanceof HTMLInputElement) {
      event.currentTarget.readOnly = false;
    }
  };
  const sheetInputProps =
    deferKeyboardFocus && !inputsEditable
      ? ({ readOnly: true, onPointerDown: unlockInputs } as const)
      : {};

  const [paypalCountries] = useState(() => PayPalCountries.transformPayPalCountriesToDropdownOptions());
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [selectedState, setSelectedState] = useState<any>(null);
  const [availableStates, setAvailableStates] = useState<any[]>([]);
  
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [useNewAddress, setUseNewAddress] = useState(false);
  const [saveAddress, setSaveAddress] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [isAddressBookReady, setIsAddressBookReady] = useState(!userId);

  const selectedAddressIdRef = useRef(selectedAddressId);

  useEffect(() => {
    selectedAddressIdRef.current = selectedAddressId;
  }, [selectedAddressId]);

  const restoredDraftRef = useRef(false);
  const [formData, setFormData] = useState<ShippingFormData>(() => {
    const empty = createEmptyFormData(profile ?? undefined);
    const draft = loadShippingDraft();
    if (draft && (draft.firstName || draft.address || draft.city)) {
      restoredDraftRef.current = true;
      return { ...empty, ...draft };
    }
    return empty;
  });
  const useNewAddressRef = useRef(useNewAddress);

  useEffect(() => {
    useNewAddressRef.current = useNewAddress;
  }, [useNewAddress]);

  const selectedAddress = savedAddresses.find((addr) => addr.id === selectedAddressId) || null;
  const requiresManualAddressForm = savedAddresses.length === 0 || useNewAddress;
  useEffect(() => {
    if (!userId) {
      setIsAddressBookReady(true);
      setSavedAddresses([]);
      setUseNewAddress(true);
      setSaveAddress(false);
      return;
    }

    let isMounted = true;
    // Abort the in-flight GET when userId flips or the component unmounts —
    // avoids state-after-unmount and stops a slow first fetch from beating
    // the second one to setSavedAddresses().
    const controller = new AbortController();
    setIsAddressBookReady(false);

    const fetchAddresses = async () => {
    setLoadingAddresses(true);
    try {
      // Boundary-parsed via @kraahl/contract — strips `user_id`/`created_at`/
      // `updated_at` extras the server returns. `SavedAddress` is structurally
      // a subset of the widened `AddressBookRow` (label nullable etc.); the
      // local interface stays because the surrounding render code already
      // tolerates the nullable fields via `?.trim()`/`|| ""` guards.
      const rows = await listAddresses({ signal: controller.signal });
        if (!isMounted) return;

        const addresses: SavedAddress[] = rows.map((r) => ({
          id: r.id,
          label: r.label ?? "",
          first_name: r.first_name ?? undefined,
          last_name: r.last_name ?? undefined,
          address: r.address,
          apartment: r.apartment ?? undefined,
          city: r.city,
          state: r.state ?? undefined,
          postal: r.postal,
          country: r.country,
          phone: r.phone ?? undefined,
          email: r.email ?? undefined,
          is_main: r.is_main,
        }));
      setSavedAddresses(addresses);

        const hadDraftRaw = restoredDraftRef.current;
        restoredDraftRef.current = false;

        /*
          Ignore sessionStorage drafts that are actually a verbatim copy of
          a saved address. Previous versions of this component persisted
          `formData` unconditionally, so selecting a saved address caused
          the next mount to see a "draft" and flip into manual mode.
          Clean up the stale entry and preselect the saved address instead.
        */
        const draftMatchesSavedAddress = hadDraftRaw && addresses.some((addr) => (
          addr.first_name === formData.firstName &&
          addr.last_name === formData.lastName &&
          addr.address === formData.address &&
          (addr.apartment ?? "") === (formData.apartment ?? "") &&
          addr.city === formData.city &&
          (addr.postal ?? "") === formData.postalCode &&
          addr.country === formData.country
        ));
        if (draftMatchesSavedAddress) {
          clearShippingDraft();
        }
        const hadDraft = hadDraftRaw && !draftMatchesSavedAddress;

        if (addresses.length === 0 && !hadDraft) {
          const empty = createEmptyFormData(profileRef.current ?? undefined);
          manualAddressDraftRef.current = {
            data: empty,
            country: null,
            state: null,
            stateOptions: [],
            shouldSave: true,
          };
          setUseNewAddress(true);
          setSaveAddress(true);
          setSelectedAddressId("");
          setSelectedCountry(null);
          setSelectedState(null);
          setAvailableStates([]);
          setFormData(empty);
        } else if (hadDraft) {
          // Draft restored — keep formData as-is, switch to new address mode
          setUseNewAddress(true);
          setSaveAddress(false);
          if (addresses.length > 0) {
            const preferred = addresses.find((addr) => addr.is_main) || addresses[0];
            setSelectedAddressId(preferred.id);
          }
        } else {
          const preferred =
            addresses.find((addr) => addr.id === selectedAddressIdRef.current) ||
            addresses.find((addr) => addr.is_main) ||
            addresses[0];

          if (useNewAddressRef.current) {
            if (preferred) {
              setSelectedAddressId(preferred.id);
            }
          } else if (preferred) {
            setSaveAddress(false);
            setSelectedAddressId(preferred.id);
            const missingFields = getMissingSavedAddressFields(preferred, profileRef.current);
            if (missingFields.length > 0) {
              autoSwitchedRef.current = true;
              setIncompleteAddressWarning(true);
              setUseNewAddress(true);
              loadAddressIntoForm(preferred);
            } else {
              setUseNewAddress(false);
              setIncompleteAddressWarning(false);
              loadAddressIntoForm(preferred);
            }
          }
      }
    } catch (error) {
      // Silent on AbortError — that's the userId-flip / unmount path, not a
      // real failure. Surface everything else so a broken `/api/address-book`
      // is at least debuggable from the browser console.
      if ((error as Error)?.name !== "AbortError") {
        console.error("Failed to fetch addresses:", error);
      }
    } finally {
        if (isMounted) {
      setLoadingAddresses(false);
          setIsAddressBookReady(true);
        }
    }
  };

    fetchAddresses();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [userId]);

  const loadAddressIntoForm = (address: SavedAddress) => {
    const { code: phoneCountryCode, number: phoneNumber } = parsePhone(address.phone);
    const countryOption = paypalCountries.find((c) => c.code === address.country);

    if (countryOption) {
      setSelectedCountry(countryOption);
      const states = PayPalCountries.getStatesForCountry(address.country);
      const stateOptions = PayPalCountries.transformStatesToDropdownOptions(states);
      setAvailableStates(stateOptions);
      if (address.state && stateOptions.length > 0) {
        const state = stateOptions.find((s) => s.code === address.state);
        setSelectedState(state || null);
      } else {
        setSelectedState(null);
      }
    } else {
      setSelectedCountry(null);
      setAvailableStates([]);
      setSelectedState(null);
    }

    const profileSnapshot = profileRef.current;
    
    const newFormData = {
      firstName: address.first_name || "",
      lastName: address.last_name || "",
      address: address.address,
      apartment: address.apartment || "",
      city: address.city,
      postalCode: address.postal,
      country: address.country,
      state: address.state || "",
      email: address.email || profileSnapshot?.email || "",
      phoneCountryCode,
      phoneNumber,
    };
    
    setFormData(newFormData);
    setSaveAddress(false);
    
    if (onCountryChangeAction) {
      onCountryChangeAction(address.country);
    }
  };

  useEffect(() => {
    if (!useNewAddress) return;
    manualAddressDraftRef.current = {
      data: formData,
      country: selectedCountry,
      state: selectedState,
      stateOptions: availableStates,
      shouldSave: saveAddress,
    };
  }, [formData, selectedCountry, selectedState, availableStates, saveAddress, useNewAddress]);

  useEffect(() => {
    if (!requiresManualAddressForm) return;
    const { code, number } = parsePhone(profile?.phone);
    setFormData((prev) => ({
      ...prev,
      email: profile?.email ?? prev.email,
      ...(profile?.phone ? { phoneCountryCode: code, phoneNumber: number } : {}),
    }));
  }, [profile?.email, profile?.phone, requiresManualAddressForm]);

  useEffect(() => {
    if (formData.country && !selectedCountry) {
      const country = paypalCountries.find((c) => c.code === formData.country);
      if (country) {
        setSelectedCountry(country);
        const states = PayPalCountries.getStatesForCountry(formData.country);
        const stateOptions = PayPalCountries.transformStatesToDropdownOptions(states);
        setAvailableStates(stateOptions);
        if (formData.state && stateOptions.length > 0) {
          const state = stateOptions.find((s) => s.code === formData.state);
          if (state) setSelectedState(state);
        }
      }
    }
  }, [formData.country, selectedCountry]);

  const handleCountrySelect = (option: any) => {
    if (!option.code) return;
    setSelectedCountry(option);
    
    const newFormData = {
      ...formData,
      country: option.code,
      state: "",
    };
    setFormData(newFormData);
    
    if (onCountryChangeAction) {
      onCountryChangeAction(option.code);
    }
    
    const states = PayPalCountries.getStatesForCountry(option.code);
    const stateOptions = PayPalCountries.transformStatesToDropdownOptions(states);
    setAvailableStates(stateOptions);
    setSelectedState(null);
  };

  const handleStateSelect = (option: any) => {
    setSelectedState(option);
    setFormData((prev) => ({
      ...prev,
      state: option.code || "",
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const inputs = containerRef.current.querySelectorAll<HTMLInputElement>("[required]");
    const basicValid = Array.from(inputs).every((input) => input.checkValidity()) && !!formData.country;
    const stateValid = !isStateRequiredForCountry(formData.country) || !!formData.state;
    const phoneValid = !!(formData.phoneCountryCode && formData.phoneNumber && formData.phoneNumber.trim().length >= 4);
    const valid = basicValid && stateValid && phoneValid;
    onFormDataChangeAction(valid, formData, saveAddress);
  }, [formData, saveAddress, onFormDataChangeAction]);

  /*
    Only persist the draft while the user is in "Choose another address"
    (manual) mode. Persisting the content of a selected saved address would
    make `loadShippingDraft` return truthy next time the sheet opens, and
    `fetchAddresses` would then flip `useNewAddress` to true — defeating the
    whole point of preselecting the main saved address.
  */
  useEffect(() => {
    if (useNewAddress) {
      saveShippingDraft(formData);
    }
  }, [formData, useNewAddress]);

  const handleAddressSelect = (addressId: string) => {
    if (useNewAddress) return;
    autoSwitchedRef.current = false;
    setSelectedAddressId(addressId);
    const address = savedAddresses.find((addr) => addr.id === addressId);
    if (address) {
      loadAddressIntoForm(address);
      const missingFields = getMissingSavedAddressFields(address, profileRef.current);
      if (missingFields.length > 0) {
        setIncompleteAddressWarning(true);
        setUseNewAddress(true);
      } else {
        setIncompleteAddressWarning(false);
        setUseNewAddress(false);
      }
    }
  };

  const handleUseNewAddress = (checked: boolean) => {
    // Whenever the user explicitly toggles this switch, the auto-switch warning is no longer relevant
    autoSwitchedRef.current = false;
    setIncompleteAddressWarning(false);
    if (checked) {
      const draft = manualAddressDraftRef.current;
      if (draft) {
        setFormData(draft.data);
        setSelectedCountry(draft.country);
        setSelectedState(draft.state);
        setAvailableStates(draft.stateOptions);
        setSaveAddress(draft.shouldSave);
    } else {
        const empty = createEmptyFormData(profileRef.current ?? undefined);
        setFormData(empty);
        setSelectedCountry(null);
        setSelectedState(null);
        setAvailableStates([]);
        setSaveAddress(false);
      }
      setUseNewAddress(true);
      return;
    }

    manualAddressDraftRef.current = {
      data: formData,
      country: selectedCountry,
      state: selectedState,
      stateOptions: availableStates,
      shouldSave: saveAddress,
    };

    setUseNewAddress(false);
    setSaveAddress(false);
    clearShippingDraft();

    const fallback =
      savedAddresses.find((addr) => addr.id === selectedAddressId) ||
      savedAddresses.find((addr) => addr.is_main) ||
      savedAddresses[0];

    if (fallback) {
      setSelectedAddressId(fallback.id);
      loadAddressIntoForm(fallback);
    } else {
      const empty = createEmptyFormData(profileRef.current ?? undefined);
      setFormData(empty);
    }
  };

  return (
    /* No `dark` here any more: the scope belongs to the host panel
       (`SHEET_PANEL_DARK` / `DIALOG_CARD_CONTENT_BASE` / the cart drawer), and
       re-declaring it locally would survive the step-2f flip as a dark island
       on a light sheet. `data-dark-inputs` stays — it is the hook for the
       autofill override in `app/globals.css`, which is now written in the same
       `--sheet*` tokens and therefore follows the panel too. */
    <div ref={containerRef} className="grid gap-4" data-dark-inputs="">
      {!isAddressBookReady && (
        <div className="space-y-4">
          <Skeleton className="h-12 rounded-full" />
          <Skeleton className="h-36 rounded-3xl" />
          <Skeleton className="h-12 rounded-full" />
        </div>
      )}

      {isAddressBookReady && savedAddresses.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Address book
            </Label>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={useNewAddress || loadingAddresses || savedAddresses.length <= 1}
                className="flex w-full items-center justify-between overflow-hidden rounded-3xl border px-4 py-3 text-left transition focus:outline-hidden focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60 border-sheet-handle bg-sheet text-foreground focus:ring-muted-foreground/70"
              >
                <div className="min-w-0 max-w-full flex-1 space-y-1">
                  <p className="text-sm font-semibold leading-tight line-clamp-1 wrap-break-word">
                    {selectedAddress ? (
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="line-clamp-1 wrap-break-word">{selectedAddress.label}</span>
                        {selectedAddress.is_main && (
                          <span className="shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-500">
                            Main
                          </span>
                        )}
                      </span>
                    ) : (
                      t("chooseAddress")
                    )}
                  </p>
                  <p className="text-xs line-clamp-2 wrap-break-word text-muted-foreground">
                    {selectedAddress
                      ? formatAddressPreview(selectedAddress)
                      : t("selectSavedAddress")}
                  </p>
                </div>
                {loadingAddresses ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            </DropdownMenuTrigger>
            {/* `surface="dialog"` is load-bearing, not tidying — but for the
                Z RUNG, not the colour (since 2f both surfaces follow the
                theme). `dialog` normalises to the chrome surface, which paints
                the `--sheet*` set (matches the panel this menu drops out of,
                vs `panel`'s popover ladder) AND raises the portal to
                `z-[10140]`, above the sheet — the hand-rolled predecessor
                inherited `z-1500`, below the overlay tier, and opened BEHIND
                the sheet. Same call the sibling select in `payment-sheet.tsx`
                makes. @see .tondocs/ui/DROPDOWNS.md */}
            <DropdownMenuContent
              align="start"
              surface="dialog"
              className="w-(--radix-dropdown-menu-trigger-width) space-y-1 max-h-[70vh] overflow-y-auto rounded-3xl"
            >
                  {savedAddresses.map((addr) => (
                <DropdownMenuItem
                  key={addr.id}
                  disabled={useNewAddress}
                  onSelect={() => handleAddressSelect(addr.id)}
                  className="flex w-full items-center justify-between gap-3 rounded-2xl overflow-hidden"
                >
                  <div className="min-w-0 flex-1 space-y-1 text-left">
                    <div className="flex items-center gap-2">
                      <p className="min-w-0 flex-1 text-sm font-semibold leading-tight line-clamp-1 wrap-break-word">
                        {addr.label}
                      </p>
                      {addr.is_main && (
                        <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-500">
                          Main
                        </span>
                      )}
                    </div>
                    <p className="text-xs line-clamp-2 wrap-break-word text-muted-foreground">
                      {formatAddressPreview(addr)}
                    </p>
                  </div>
                  {selectedAddressId === addr.id && <CheckCircle2 className="h-4 w-4 text-primary" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex w-full items-center justify-between h-12 rounded-3xl border px-4 border-sheet-handle bg-sheet">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">Choose another address</p>
              <p className="text-xs text-muted-foreground">Enter a different address just for this order</p>
            </div>
            <Switch
              id="use-new-address"
              checked={useNewAddress}
              onCheckedChange={(checked) => handleUseNewAddress(!!checked)}
              aria-label={t("chooseAnotherAddress")}
            />
          </div>
        </div>
      )}

      {isAddressBookReady && requiresManualAddressForm && incompleteAddressWarning && (
        <div
          className="flex items-start gap-3 rounded-3xl border px-4 py-3 border-amber-400/50 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-sm leading-snug">{t("incompleteAddressWarning")}</p>
        </div>
      )}

      {isAddressBookReady && requiresManualAddressForm && (
        <>
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
              <Label htmlFor="firstName" className={cn("text-base", sheetLabelClass)}>
                {t("firstName")}
              </Label>
          <Input
            id="firstName"
            name="firstName"
            required
            autoComplete="shipping given-name"
                className={cn("h-12 rounded-3xl px-4 text-base", sheetInputClass)}
            value={formData.firstName}
            onChange={handleChange}
            {...sheetInputProps}
          />
        </div>
        <div className="grid gap-2">
              <Label htmlFor="lastName" className={cn("text-base", sheetLabelClass)}>
                {t("lastName")}
              </Label>
          <Input
            id="lastName"
            name="lastName"
            required
            autoComplete="shipping family-name"
                className={cn("h-12 rounded-3xl px-4 text-base", sheetInputClass)}
            value={formData.lastName}
            onChange={handleChange}
            {...sheetInputProps}
          />
        </div>
      </div>

      <div className="grid gap-2">
            <Label htmlFor="address" className={cn("text-base", sheetLabelClass)}>
              {t("streetAddress")}
            </Label>
        <Input
          id="address"
          name="address"
          required
          autoComplete="shipping street-address"
              className={cn("h-12 rounded-3xl px-4 text-base", sheetInputClass)}
          value={formData.address}
          onChange={handleChange}
          {...sheetInputProps}
        />
      </div>

      <div className="grid gap-2">
            <Label htmlFor="apartment" className={cn("text-base", sheetLabelClass)}>
              {t("aptSuite")}
            </Label>
        <Input
          id="apartment"
          name="apartment"
          autoComplete="shipping address-line2"
              className={cn("h-12 rounded-3xl px-4 text-base", sheetInputClass)}
          value={formData.apartment}
          onChange={handleChange}
          {...sheetInputProps}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
              <Label htmlFor="city" className={cn("text-base", sheetLabelClass)}>
                {t("city")}
              </Label>
          <Input
            id="city"
            name="city"
            required
            autoComplete="shipping address-level2"
                className={cn("h-12 rounded-3xl px-4 text-base", sheetInputClass)}
            value={formData.city}
            onChange={handleChange}
            {...sheetInputProps}
          />
        </div>
        <div className="grid gap-2">
              <Label htmlFor="postalCode" className={cn("text-base", sheetLabelClass)}>
                {t("postalCode")}
              </Label>
          <Input
            id="postalCode"
            name="postalCode"
            required
            autoComplete="shipping postal-code"
                className={cn("h-12 rounded-3xl px-4 text-base", sheetInputClass)}
            value={formData.postalCode}
            onChange={handleChange}
            {...sheetInputProps}
          />
        </div>
      </div>

      <div className="grid gap-2">
            <Label htmlFor="country" className={cn("text-base", sheetLabelClass)}>
              {t("country")}
            </Label>
        <SearchableDropdown
          options={paypalCountries}
          label="name"
          selectedValue={selectedCountry}
          onSelect={(country) => handleCountrySelect(country)}
          placeholder={t("selectCountry")}
          className="h-12"
          sheetTitle={t("selectCountry")}
        />
      </div>

      {formData.country && isStateRequiredForCountry(formData.country) && (
        <div className="grid gap-2">
          <Label htmlFor="state" className={cn("text-base", sheetLabelClass)}>
            {formData.country === "US" ? t("state") : t("province")}
          </Label>
          <SearchableDropdown
            options={availableStates}
            label="name"
            selectedValue={selectedState}
            onSelect={handleStateSelect}
            placeholder={formData.country === "US" ? t("selectState") : t("selectProvince")}
            sheetTitle={formData.country === "US" ? t("selectState") : t("selectProvince")}
            className="h-12"
            showFlag={false}
          />
        </div>
      )}

      {formData.country && !isStateRequiredForCountry(formData.country) && (
        <div className="grid gap-2">
              <Label htmlFor="state" className={cn("text-base", sheetLabelClass)}>
                {t("stateOrRegion")}
              </Label>
          <Input
            id="state"
            name="state"
            autoComplete="shipping address-level1"
                className={cn("h-12 rounded-3xl px-4 text-base", sheetInputClass)}
            placeholder={t("enterStateRegion")}
            value={formData.state}
            onChange={handleChange}
            {...sheetInputProps}
          />
        </div>
      )}

      <div className="grid gap-2">
            <Label htmlFor="email" className={cn("text-base", sheetLabelClass)}>
              {t("email")}
            </Label>
        <Input
          id="email"
          name="email"
          type="email"
          required
          autoComplete="email"
              className={cn("h-12 rounded-3xl px-4 text-base", sheetInputClass)}
          placeholder={t("emailPlaceholder")}
          value={formData.email}
          onChange={handleChange}
          {...sheetInputProps}
        />
      </div>

      <div className="grid gap-2">
            <Label htmlFor="phone" className={cn("text-base", sheetLabelClass)}>
              {t("phone")}
            </Label>
        <PhoneInput
          name="phone"
          placeholder={t("enterPhone")}
          value={formData.phoneNumber}
          countryCode={formData.phoneCountryCode}
          onChange={(value, country) => {
            setFormData((prev) => ({
              ...prev,
              phoneCountryCode: country.code,
              phoneNumber: value,
            }));
          }}
          className="h-12 text-base"
          /* Not "force dark" any more — `PhoneInput`'s branch was moved onto the
             `--sheet*` tokens in step 2d and the prop renamed `dark` -> `onSheet`
             in 2c-ter, so this selects the same on-panel field dialect
             `sheetInputClass` gives every other input. */
          onSheet
          deferKeyboardFocus={deferKeyboardFocus && !inputsEditable}
          onUnlockKeyboardFocus={unlockInputs}
        />
      </div>

          <div className="flex items-center justify-between rounded-2xl border px-4 py-3 border-sheet-handle bg-sheet">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">Save for future orders</p>
              <p className="text-xs text-muted-foreground">Adds this location to your address book</p>
            </div>
            <Switch
              id="save-address"
              checked={saveAddress}
              onCheckedChange={(checked) => setSaveAddress(!!checked)}
              aria-label={t("saveForFuture")}
            />
          </div>
        </>
      )}
    </div>
  );
}
